<template>
  <div class="analysis-report-root">

  <!-- Compact summary strip for My view (embedded in Call History tab) -->
  <div
    v-if="layout === 'compact-summary' && isLoading"
    class="quality-summary-strip quality-summary-strip--loading"
  >
    <b-spinner small variant="primary" />
    <span class="quality-summary-strip__loading">Loading full-range KPIs…</span>
  </div>
  <div
    v-else-if="layout === 'compact-summary' && analyzedCalls.length"
    class="quality-summary-strip"
  >
    <span class="quality-summary-strip__title">
      <feather-icon icon="CpuIcon" size="16" class="text-primary" />
      <span>Your Call Quality</span>
    </span>
    <span class="quality-summary-strip__metric">
      Avg Hygiene:
      <strong class="text-primary">{{ formatScorePercent(teamAverageScore) }}</strong>
    </span>
    <span class="quality-summary-strip__metric">
      Missed Checks:
      <strong class="text-body">{{ totalMissedChecks }}</strong>
    </span>
    <span class="quality-summary-strip__metric">
      Red Flags:
      <strong :class="totalRedFlags ? 'text-danger' : 'text-body'">{{ totalRedFlags }}</strong>
    </span>
    <span class="quality-summary-strip__metric">
      Analyzed:
      <strong class="text-body">{{ analyzedCalls.length }}</strong>
    </span>
  </div>

  <b-card v-else-if="layout !== 'compact-summary'" no-body>

    <b-card-header
      v-if="layout !== 'analysis-panel'"
      class="d-flex flex-wrap align-items-center py-1"
      style="gap:6px; border-bottom:1px solid #ebe9f1;"
    >

      <h5 class="mb-0 flex-shrink-0">

        <feather-icon icon="CpuIcon" size="16" class="mr-50 text-primary" />

        {{ reportTitle }}

        <b-badge variant="light-secondary" class="ml-50">{{ analyzedCalls.length }}</b-badge>

      </h5>

      <div v-if="teamAverageScore != null && viewMode !== 'my'" class="d-flex align-items-center ml-md-2" style="gap:6px;">

        <small class="text-muted">Avg hygiene:</small>

        <span class="small font-weight-bold text-body">{{ formatScorePercent(teamAverageScore) }}</span>

      </div>

      <small class="text-muted ml-auto d-none d-md-inline">

        {{ reportSubtitle }}

      </small>

    </b-card-header>



    <div v-if="isLoading" class="text-center py-4">

      <b-spinner variant="primary" />

      <p class="mt-1 text-muted mb-0">Loading call analysis…</p>

    </div>



    <div v-else-if="!analyzedCalls.length" class="text-center py-4 text-muted">

      <feather-icon icon="FileTextIcon" size="32" class="mb-1 text-muted" />

      <p class="mb-25 font-weight-bold">{{ emptyStateTitle }}</p>

      <small>{{ emptyStateMessage }}</small>

    </div>



    <div v-else-if="viewMode !== 'my'" class="table-responsive mb-0 pt-1">
      <table class="table table-sm mb-0 recruiter-summary-table" style="font-size:0.82rem;">
        <thead style="background:#f0f0f5;">
          <tr>
            <th class="px-1 py-50 text-muted font-weight-bold">Recruiter</th>
            <th class="px-1 py-50 text-center text-muted font-weight-bold">Total Analyzed</th>
            <th class="px-1 py-50 text-center text-muted font-weight-bold">Fresh Call</th>
            <th class="px-1 py-50 text-center text-muted font-weight-bold">Follow-up</th>
            <th class="px-1 py-50 text-center text-muted font-weight-bold">Avg Hygiene</th>
            <th class="px-1 py-50 text-center text-muted font-weight-bold">Flags</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in recruiterSummary"
            :key="row.empId"
          >
            <td class="px-1 py-50 align-middle">
              <span class="font-weight-bold">{{ row.name }}</span>
              <small class="d-block text-muted">{{ row.empId }}</small>
            </td>
            <td class="px-1 py-50 text-center align-middle font-weight-bold">{{ row.total }}</td>
            <td class="px-1 py-50 text-center align-middle">
              <b-badge variant="light-primary" class="px-75">{{ row.freshCall }}</b-badge>
            </td>
            <td class="px-1 py-50 text-center align-middle">
              <b-badge variant="light-secondary" class="px-75">{{ row.followUp }}</b-badge>
            </td>
            <td class="px-1 py-50 text-center align-middle">
              <span
                v-if="row.avgHygiene != null"
                class="font-weight-bold small"
              >{{ formatScorePercent(row.avgHygiene) }}</span>
              <span v-else class="text-muted">—</span>
            </td>
            <td class="px-1 py-50 text-center align-middle">
              <b-badge
                v-if="row.flagCount > 0"
                variant="light-danger"
                class="px-75 flags-count-badge"
                role="button"
                tabindex="0"
                :aria-label="`${row.flagCount} flags for ${row.name}. Open details.`"
                @click="openFlagsModal(row)"
                @keydown.enter.prevent="openFlagsModal(row)"
                @keydown.space.prevent="openFlagsModal(row)"
              >{{ row.flagCount }}</b-badge>
              <span v-else class="text-muted">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </b-card>

  <b-modal
    v-model="flagsModal.show"
    :title="flagsModalTitle"
    ok-only
    ok-title="Close"
    scrollable
    body-class="flags-modal-body"
    :no-enforce-focus="analysisModal.show"
    @hidden="resetFlagsModal"
  >
    <div v-if="flagsModal.row" class="flags-modal">
      <p class="mb-50">
        <strong>{{ flagsModal.row.name }}</strong>
        <small class="d-block text-muted">{{ flagsModal.row.empId }}</small>
      </p>
      <p v-if="periodLabel" class="small text-muted mb-1">{{ periodLabel }}</p>
      <p class="small mb-1">
        <span class="text-muted">Total flags:</span>
        <strong class="ml-25">{{ flagsModalDetail.total }}</strong>
      </p>

      <div
        v-if="flagRecording.url || flagRecording.loading || flagRecording.error"
        class="flags-player-bar"
      >
        <div class="flags-player-bar__meta">
          <feather-icon icon="PlayCircleIcon" size="14" class="text-info" />
          <span class="flags-player-bar__title">
            <strong>{{ flagRecording.candidate || 'Recording' }}</strong>
            <span v-if="flagRecording.date" class="text-muted"> · {{ flagRecording.date }}</span>
          </span>
        </div>
        <div v-if="flagRecording.loading" class="flags-player-bar__status text-muted">
          <b-spinner small class="mr-50" />
          Loading recording…
        </div>
        <div
          v-else-if="flagRecording.error"
          class="flags-player-bar__status flags-player-bar__status--error"
        >
          <span>{{ flagRecording.error }}</span>
          <b-button variant="link" size="sm" class="p-0" @click="retryFlagRecording">
            Try again
          </b-button>
        </div>
        <audio
          v-else-if="isPlayableRecordingUrl(flagRecording.url)"
          class="flags-player-bar__audio"
          controls
          autoplay
          :src="flagRecording.url"
        >
          Your browser does not support audio playback.
        </audio>
      </div>

      <div
        v-for="group in flagsModalDetail.groups"
        :key="group.key"
        class="flags-group"
      >
        <div class="flags-group-head">
          <b-badge variant="light-danger" class="flags-modal-label">{{ group.label }}</b-badge>
          <b-badge variant="light-secondary">{{ group.count }}</b-badge>
          <small class="text-muted">{{ callFlagTypeLabel(group.type) }}</small>
        </div>
        <ul v-if="group.examples.length" class="flags-example-list">
          <li
            v-for="(ex, idx) in group.examples"
            :key="exampleKey(group, ex, idx)"
            class="flags-example-item"
          >
            <div class="flags-example-row">
              <button
                type="button"
                class="flags-example-btn"
                :aria-label="`Open analysis for ${ex.candidate}`"
                @click="openExampleAnalysis(ex)"
              >
                <span class="flags-example-text">
                  {{ ex.candidate }}
                  <span class="text-muted"> · {{ ex.date }}</span>
                </span>
              </button>
              <span class="flags-play-col">
                <b-button
                  variant="outline-info"
                  size="sm"
                  class="flags-play-btn"
                  :aria-label="`Play recording for ${ex.candidate}`"
                  :disabled="isFlagRecordingBusy(ex)"
                  @click.stop="playFlagRecording(ex)"
                >
                  <b-spinner v-if="isFlagRecordingBusy(ex)" small class="mr-25" />
                  <feather-icon
                    v-else
                    icon="PlayCircleIcon"
                    size="13"
                    class="mr-25"
                  />
                  Play
                </b-button>
              </span>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </b-modal>

  <call-analysis-detail-modal
    v-model="analysisModal.show"
    :call="analysisModal.call"
    :view-mode="viewMode"
    @hidden="analysisModal.call = null"
  />
  </div>
</template>



<script>

import {

  BCard,

  BCardHeader,

  BBadge,

  BSpinner,

  BModal,

  BButton,

} from 'bootstrap-vue'
import callAnalyticsService from '@/services/call-analytics-service'
import { hasRecordingAvailable, isValidRecordingUrl } from '@/utils/call-recording-helpers'

import {

  parseAnalysis,

  getRedFlags,

  getMissedHygieneChecks,

  averageHygieneScore,

  formatScorePercent,

  groupAnalysisByRecruiter,

  groupCallFlags,

  callFlagTypeLabel,

} from '@/utils/call-analysis-helpers'
import CallAnalysisDetailModal from './CallAnalysisDetailModal.vue'

function emptyFlagRecording() {
  return {
    key: '',
    candidate: '',
    date: '',
    url: null,
    error: null,
    loading: false,
  }
}

export default {

  name: 'MyCallsAnalysisReport',



  components: {

    BCard,

    BCardHeader,

    BBadge,

    BSpinner,

    BModal,

    BButton,

    CallAnalysisDetailModal,

  },

  props: {

    analyzedCalls: {

      type: Array,

      default: () => [],

    },

    recruiterNameMap: {

      type: Object,

      default: () => ({}),

    },

    viewMode: {

      type: String,

      default: 'my',

    },

    isLoading: {

      type: Boolean,

      default: false,

    },

    layout: {

      type: String,

      default: 'analysis-panel',

      validator: val => ['compact-summary', 'analysis-panel'].includes(val),

    },

    periodStart: {

      type: String,

      default: '',

    },

    periodEnd: {

      type: String,

      default: '',

    },

  },

  data() {
    return {
      flagsModal: {
        show: false,
        row: null,
      },
      analysisModal: {
        show: false,
        call: null,
      },
      flagRecording: emptyFlagRecording(),
      _flagRecordingGen: 0,
    }
  },

  computed: {

    reportTitle() {

      if (this.viewMode === 'my') return 'Your Call Quality'

      if (this.viewMode === 'org') return 'Call Analysis — Organization'

      return 'Call Analysis — Team'

    },



    reportSubtitle() {

      if (this.viewMode === 'my') {

        return 'Review AI feedback on your answered calls to improve hygiene and outcomes'

      }

      return 'AI analysis runs automatically after answered calls'

    },



    emptyStateTitle() {

      return this.viewMode === 'my'

        ? 'No analyzed calls yet'

        : 'No analyzed calls in this period'

    },



    emptyStateMessage() {

      return this.viewMode === 'my'

        ? 'Complete answered calls and check back once recordings have been processed for quality feedback.'

        : 'Analysis is generated after answered calls complete. Check back once recordings have been processed.'

    },



    teamAverageScore() {

      return averageHygieneScore(this.analyzedCalls)

    },



    totalMissedChecks() {

      return this.analyzedCalls.reduce((sum, call) => {

        const analysis = parseAnalysis(call)

        return sum + getMissedHygieneChecks(analysis).length

      }, 0)

    },



    totalRedFlags() {

      return this.analyzedCalls.reduce((sum, call) => {

        return sum + getRedFlags(parseAnalysis(call)).length

      }, 0)

    },



    recruiterSummary() {

      return groupAnalysisByRecruiter(this.analyzedCalls, this.recruiterNameMap)

    },

    periodLabel() {
      const start = String(this.periodStart || '').trim()
      const end = String(this.periodEnd || '').trim()
      if (start && end && start !== end) return `${start} – ${end}`
      return start || end || ''
    },

    flagsModalTitle() {
      const name = this.flagsModal.row && this.flagsModal.row.name
      return name ? `Flags — ${name}` : 'Flags'
    },

    flagsModalCalls() {
      const empId = this.flagsModal.row && this.flagsModal.row.empId
      if (!empId) return []
      return this.analyzedCalls.filter(call => String(call._rawRecruiter || '') === String(empId))
    },

    flagsModalDetail() {
      return groupCallFlags(this.flagsModalCalls)
    },

  },



  methods: {

    formatScorePercent,
    callFlagTypeLabel,

    openFlagsModal(row) {
      if (!row || !row.flagCount) return
      this._flagRecordingGen += 1
      this.flagRecording = emptyFlagRecording()
      this.flagsModal = { show: true, row }
    },

    resetFlagsModal() {
      this._flagRecordingGen += 1
      this.flagsModal = { show: false, row: null }
      this.analysisModal = { show: false, call: null }
      this.flagRecording = emptyFlagRecording()
    },

    exampleKey(group, ex, idx) {
      const call = ex && ex.call
      const id = call && (call.sid || call.callDbId || call.rowKey)
      return `${group.key}-${id || idx}`
    },

    exampleCallKey(ex) {
      const call = ex && ex.call
      return String((call && (call.sid || call.callDbId || call.rowKey)) || ex.candidate || '')
    },

    isFlagRecordingBusy(ex) {
      return this.flagRecording.loading && this.flagRecording.key === this.exampleCallKey(ex)
    },

    isPlayableRecordingUrl(url) {
      return isValidRecordingUrl(url)
    },

    retryFlagRecording() {
      const key = this.flagRecording.key
      if (!key) return
      const match = this.flagsModalDetail.groups
        .flatMap(group => group.examples)
        .find(ex => this.exampleCallKey(ex) === key)
      if (match) this.playFlagRecording(match)
    },

    async playFlagRecording(ex) {
      const call = ex && ex.call
      const key = this.exampleCallKey(ex)
      if (!call || !hasRecordingAvailable(call)) {
        this.flagRecording = {
          key,
          candidate: (ex && ex.candidate) || '',
          date: (ex && ex.date) || '',
          url: null,
          error: 'No recording available for this call.',
          loading: false,
        }
        return
      }
      const gen = (this._flagRecordingGen += 1)
      this.flagRecording = {
        key,
        candidate: ex.candidate || '',
        date: ex.date || '',
        url: null,
        error: null,
        loading: true,
      }
      try {
        const res = await callAnalyticsService.resolveRecordingPlayUrl({
          recordingS3Key: call.recordingS3Key || null,
          sid: call.sid || null,
          callDbId: call.callDbId || null,
          item: call,
        })
        if (gen !== this._flagRecordingGen) return
        if (res && res.status === 'OK' && isValidRecordingUrl(res.url)) {
          this.flagRecording = {
            ...this.flagRecording,
            url: res.url,
            error: null,
            loading: false,
          }
          return
        }
        this.flagRecording = {
          ...this.flagRecording,
          url: null,
          error: (res && res.message) || 'No recording available for this call.',
          loading: false,
        }
      } catch (err) {
        if (gen !== this._flagRecordingGen) return
        this.flagRecording = {
          ...this.flagRecording,
          url: null,
          error: 'Could not load recording. Please try again.',
          loading: false,
        }
      }
    },

    /** Modal fetches API 2 for this call — do not assume the FLAGS example row has full analysis. */
    openExampleAnalysis(ex) {
      const call = ex && ex.call
      if (!call) return
      this.analysisModal.call = call
      this.analysisModal.show = true
    },

  },

}

</script>

<style scoped>
.quality-summary-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 22px;
  margin-bottom: 0.75rem;
  padding: 0.65rem 1rem 0.65rem 0.9rem;
  background: #fff;
  border: 1px solid rgba(0, 172, 153, 0.28);
  border-left: 4px solid #00ac99;
  border-radius: 0.4285rem;
  box-shadow: 0 2px 8px rgba(0, 172, 153, 0.1);
}

.quality-summary-strip--loading {
  gap: 8px;
}

.quality-summary-strip__title {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.2;
  color: #2c2c2c;
}

.quality-summary-strip__metric,
.quality-summary-strip__loading {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4b4b4b;
}

.quality-summary-strip__metric strong {
  font-weight: 700;
  margin-left: 2px;
}

.flags-count-badge {
  cursor: pointer;
}

.flags-count-badge:focus {
  outline: 2px solid #ea5455;
  outline-offset: 2px;
}

.flags-modal-label {
  white-space: normal;
  text-align: left;
  line-height: 1.35;
}

.flags-player-bar {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 5.25rem;
  margin: 0 0 0.75rem;
  padding: 0.55rem 0.7rem;
  background: #fff;
  border: 1px solid rgba(0, 172, 153, 0.28);
  border-radius: 0.4285rem;
  box-shadow: 0 1px 4px rgba(0, 172, 153, 0.08);
}

.flags-player-bar__meta {
  display: flex;
  align-items: center;
  min-width: 0;
  margin-bottom: 0.3rem;
  gap: 6px;
}

.flags-player-bar__title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.8125rem;
}

.flags-player-bar__status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-height: 2rem;
  gap: 8px;
  font-size: 0.8125rem;
}

.flags-player-bar__status--error {
  color: #ea5455;
}

.flags-player-bar__audio {
  width: 100%;
  height: 32px;
  outline: none;
}

.flags-group {
  margin-bottom: 0.65rem;
  padding-bottom: 0.55rem;
  border-bottom: 1px solid #eee;
}

.flags-group:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: 0;
}

.flags-group-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 0.35rem;
  gap: 6px;
}

.flags-example-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.flags-example-item {
  margin: 0;
}

.flags-example-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  min-height: 2rem;
  padding: 0.15rem 0;
  gap: 8px;
}

.flags-example-btn {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  font-size: 0.8125rem;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;
}

.flags-example-btn::before {
  content: '•';
  flex-shrink: 0;
  margin-right: 0.4rem;
  color: #6e6b7b;
}

.flags-example-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flags-play-col {
  display: flex;
  flex: 0 0 5.25rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: flex-end;
}

.flags-play-btn {
  padding: 0.12rem 0.45rem;
  line-height: 1.15;
  font-size: 0.75rem;
  white-space: nowrap;
}

.flags-example-btn:hover {
  color: #00ac99;
}

.flags-example-btn:hover .flags-example-text {
  text-decoration: underline;
}

.flags-example-btn:focus {
  outline: 2px solid #00ac99;
  outline-offset: 2px;
}
</style>

<style>
.modal-body.flags-modal-body {
  overflow-x: hidden;
}
</style>
